import type { ModuleKey } from '@alicerce/contracts';
import { Badge, Button, Card, CardBody, EmptyState, Input, PageHeader } from '@alicerce/ui';
import { Compass } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { startTour } from '@/components/setup/setup-guide';
import { useCurrent } from '@/lib/session';

interface Step {
  text: string;
  link?: { to: string; label: string };
}

interface Topic {
  module: ModuleKey;
  title: string;
  intro: string;
  steps: Step[];
  faq?: { q: string; a: string }[];
}

/** Central de ajuda (guia 19.2): os passos de configuração de cada módulo, com atalho para a tela. */
export const HELP_TOPICS: Topic[] = [
  {
    module: 'core',
    title: 'Primeiros passos',
    intro: 'O básico que todo negócio configura, com ou sem loja online.',
    steps: [
      { text: 'Preencha nome, CPF/CNPJ e regime tributário.', link: { to: '/configuracoes/negocio', label: 'Dados do negócio' } },
      { text: 'Envie o logo e escolha as cores; elas aparecem na loja e nos e-mails.', link: { to: '/configuracoes/negocio', label: 'Logo e cores' } },
      { text: 'Cadastre os produtos um a um ou importe uma planilha.', link: { to: '/produtos/importar', label: 'Importar planilha' } },
      { text: 'Convide quem trabalha com você e escolha o papel de cada um.', link: { to: '/equipe', label: 'Equipe' } },
      { text: 'Ative a verificação em duas etapas.', link: { to: '/configuracoes/seguranca', label: 'Segurança' } },
    ],
    faq: [
      { q: 'Vendi pelo WhatsApp. Como registro?', a: 'Use a Venda rápida: escolha os produtos, a forma de pagamento e pronto. A venda entra no resumo e baixa o estoque, se você usa o módulo de estoque.' },
      { q: 'Qual papel dar para o meu contador?', a: 'Financeiro. Ele vê gastos, lucros e notas, mas não mexe em produtos nem pedidos.' },
    ],
  },
  {
    module: 'storefront',
    title: 'Loja online',
    intro: 'Sua loja fica em seunegocio.alicerce.app logo de cara. Domínio próprio é opcional.',
    steps: [
      { text: 'Escolha o tema e ajuste o visual no editor, com a prévia ao lado.', link: { to: '/loja', label: 'Editor da loja' } },
      { text: 'Crie as páginas "Sobre" e "Trocas e devoluções".', link: { to: '/loja/paginas', label: 'Páginas' } },
      { text: 'Para usar seu domínio, adicione-o e crie o registro CNAME que mostramos. O HTTPS sai sozinho.', link: { to: '/loja/dominios', label: 'Domínios' } },
    ],
    faq: [{ q: 'Meu domínio não ativa.', a: 'O DNS pode levar algumas horas para propagar. Confira se o CNAME aponta exatamente para o endereço mostrado na tela de domínios. Você recebe um aviso quando ele ficar ativo.' }],
  },
  {
    module: 'checkout',
    title: 'Pagamentos e frete',
    intro: 'Pix, cartão e boleto caem na sua conta de recebimento, já descontadas as taxas.',
    steps: [
      { text: 'Crie a conta de recebimento e envie os documentos pedidos.', link: { to: '/checkout/recebimento', label: 'Conta de recebimento' } },
      { text: 'Conecte o Melhor Envio e informe o endereço de onde os pacotes saem.', link: { to: '/checkout/frete', label: 'Frete' } },
      { text: 'Se quiser, crie cupons de desconto.', link: { to: '/checkout/cupons', label: 'Cupons' } },
      { text: 'Acompanhe os pedidos pagos e compre a etiqueta direto do pedido.', link: { to: '/pedidos?status=PAID', label: 'Pedidos pagos' } },
    ],
    faq: [{ q: 'Quando o dinheiro cai?', a: 'Pix cai em até 1 dia útil; boleto, 1 dia útil depois de pago; cartão, conforme o prazo escolhido na conta de recebimento.' }],
  },
  {
    module: 'inventory',
    title: 'Estoque',
    intro: 'Cada venda baixa o estoque sozinha. Você só registra as entradas.',
    steps: [
      { text: 'Registre o saldo inicial de cada produto com uma entrada.', link: { to: '/estoque/entrada', label: 'Nova entrada' } },
      { text: 'Defina o estoque mínimo para ser avisado antes de acabar.', link: { to: '/estoque', label: 'Estoque' } },
      { text: 'Faça um inventário pelo celular de tempos em tempos para acertar diferenças.', link: { to: '/estoque/inventario', label: 'Inventário' } },
    ],
  },
  {
    module: 'finance',
    title: 'Gastos e lucros',
    intro: 'Vendas entram sozinhas. Você lança os gastos e vê quanto sobrou.',
    steps: [
      { text: 'Cadastre as despesas fixas (aluguel, internet, contador).', link: { to: '/financeiro/fixas', label: 'Despesas fixas' } },
      { text: 'Lance os outros gastos conforme acontecem; dá para anexar o comprovante.', link: { to: '/financeiro/lancamentos', label: 'Lançamentos' } },
      { text: 'Use a calculadora para chegar a um preço que dá lucro.', link: { to: '/financeiro/calculadora', label: 'Calculadora de preço' } },
      { text: 'Se você é MEI, acompanhe o limite de faturamento do ano.', link: { to: '/financeiro/mei', label: 'Limite do MEI' } },
    ],
  },
  {
    module: 'fiscal',
    title: 'Notas fiscais',
    intro: 'Com tudo configurado, a nota sai sozinha quando o pedido é pago.',
    steps: [
      { text: 'Siga a lista de configuração: endereço fiscal, regime e numeração.', link: { to: '/notas/configuracao', label: 'Configuração' } },
      { text: 'Envie o certificado digital A1 (arquivo .pfx e senha).', link: { to: '/notas/configuracao', label: 'Certificado' } },
      { text: 'Revise os perfis de imposto (NCM e CFOP) que usamos por padrão.', link: { to: '/notas/perfis', label: 'Perfis de imposto' } },
      { text: 'No fim do mês, exporte os XMLs para o contador.', link: { to: '/notas', label: 'Notas' } },
    ],
    faq: [{ q: 'Minha nota foi rejeitada.', a: 'Abra a nota: mostramos o motivo em português e o que corrigir. Depois de corrigir, clique em emitir de novo.' }],
  },
  {
    module: 'inbox',
    title: 'Atendimento',
    intro: 'WhatsApp e o chat da loja numa caixa só, com os pedidos do cliente ao lado.',
    steps: [
      { text: 'Conecte o número de WhatsApp da empresa pelo login do Facebook.', link: { to: '/atendimento/configuracao', label: 'Canais' } },
      { text: 'Crie respostas rápidas para as perguntas de sempre.', link: { to: '/atendimento/configuracao', label: 'Respostas rápidas' } },
      { text: 'Para falar com quem não escreve há mais de 24 h, use um modelo aprovado pela Meta.', link: { to: '/atendimento/configuracao', label: 'Modelos' } },
    ],
  },
  {
    module: 'ads',
    title: 'Anúncios',
    intro: 'Campanhas prontas no Instagram e Facebook, revisadas pela nossa equipe antes de ir ao ar.',
    steps: [
      { text: 'Conecte a página do Facebook e o Instagram.', link: { to: '/anuncios/conectar', label: 'Conectar' } },
      { text: 'Compre créditos de mídia; é deles que sai o valor dos anúncios.', link: { to: '/assinatura/consumo', label: 'Créditos' } },
      { text: 'Crie uma campanha: escolha os produtos, o público e o orçamento por dia.', link: { to: '/anuncios/nova', label: 'Nova campanha' } },
    ],
    faq: [{ q: 'Minha campanha voltou para ajustes.', a: 'A equipe deixa um comentário dizendo o que mudar. Ajuste e envie de novo.' }],
  },
  {
    module: 'billing',
    title: 'Assinatura',
    intro: 'Você paga só pelos módulos que usa e pode mudar quando quiser.',
    steps: [
      { text: 'Adicione ou remova módulos. Ao adicionar, paga só os dias que faltam no ciclo.', link: { to: '/assinatura', label: 'Assinatura' } },
      { text: 'Veja o consumo de notas e conversas do mês.', link: { to: '/assinatura/consumo', label: 'Consumo' } },
    ],
  },
];

const normalize = (s: string) => s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

export function HelpPage() {
  const { isModuleActive, can } = useCurrent();
  const [q, setQ] = useState('');
  const needle = normalize(q.trim());
  const topics = HELP_TOPICS.filter(
    (t) => !needle || normalize([t.title, t.intro, ...t.steps.map((s) => s.text), ...(t.faq ?? []).flatMap((f) => [f.q, f.a])].join(' ')).includes(needle),
  );
  return (
    <div className="space-y-6">
      <PageHeader
        title="Ajuda"
        description="Os passos para configurar cada parte da Gestora."
        actions={
          can('tenant:update') && (
            <Button variant="secondary" onClick={startTour}>
              <Compass className="size-4" aria-hidden /> Rever o tour do painel
            </Button>
          )
        }
      />
      <Input type="search" aria-label="Buscar na ajuda" placeholder="Buscar: nota rejeitada, domínio, frete…" value={q} onChange={(e) => setQ(e.target.value)} />
      {topics.length === 0 ? (
        <EmptyState title="Nada encontrado." description="Tente outra palavra ou fale com o suporte pelo WhatsApp." />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {topics.map((t) => (
            <Card key={t.module}>
              <CardBody className="space-y-3">
                <h2 className="flex items-center gap-2 text-lg font-semibold">
                  {t.title} {!isModuleActive(t.module) && <Badge>não contratado</Badge>}
                </h2>
                <p className="text-sm text-muted">{t.intro}</p>
                <ol className="list-decimal space-y-1.5 pl-5 text-sm">
                  {t.steps.map((s) => (
                    <li key={s.text}>
                      {s.text}{' '}
                      {s.link && (
                        <Link to={s.link.to} className="font-medium text-brand-700 hover:underline">
                          {s.link.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ol>
                {t.faq?.map((f) => (
                  <details key={f.q} className="rounded-lg bg-canvas px-3 py-2 text-sm" open={!!needle}>
                    <summary className="cursor-pointer font-medium">{f.q}</summary>
                    <p className="mt-1 text-muted">{f.a}</p>
                  </details>
                ))}
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
